import {useEffect, useRef, useState} from 'react'

import {scrollToElement} from '../../utils.js'
import {ANIMATION_DURATION, COLLAPSED_LINES} from './config.js'

const OVERFLOW_TOLERANCE = 1

const getCollapsedHeight = element => parseFloat(window.getComputedStyle(element).lineHeight) * COLLAPSED_LINES

const scrollBackToSection = element => {
  if (element.getBoundingClientRect().top >= 0) return

  scrollToElement(element)
}

const useExpandableDescription = () => {
  const contentRef = useRef(null)
  const sectionRef = useRef(null)
  const animationTimeoutRef = useRef(null)

  const [isExpanded, setIsExpanded] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [maxHeight, setMaxHeight] = useState(null)
  // Overflow can only be measured against a real layout, so the toggle ships in the
  // SSR markup and is removed after mount for the descriptions that already fit.
  const [isTruncated, setIsTruncated] = useState(true)

  useEffect(() => {
    const element = contentRef.current
    if (!element) return

    // scrollHeight reports the full text height in both states, so the same check
    // holds while the clamp is active and after it has been released.
    const measure = () => setIsTruncated(element.scrollHeight > getCollapsedHeight(element) + OVERFLOW_TOLERANCE)

    measure()
    window.addEventListener('resize', measure)

    return () => {
      window.removeEventListener('resize', measure)
      clearTimeout(animationTimeoutRef.current)
    }
  }, [])

  const scheduleAnimationEnd = () => {
    animationTimeoutRef.current = setTimeout(() => {
      setIsAnimating(false)
      setMaxHeight(null)
    }, ANIMATION_DURATION)
  }

  const toggle = () => {
    const element = contentRef.current
    clearTimeout(animationTimeoutRef.current)
    setIsAnimating(true)

    if (!isExpanded) {
      setMaxHeight(element.scrollHeight)
      setIsExpanded(true)
      scheduleAnimationEnd()
      return
    }

    // Collapsing needs the current height committed first: re-applying the clamp
    // right away would snap the box to four lines with nothing left to animate.
    setMaxHeight(element.scrollHeight)
    scrollBackToSection(sectionRef.current)
    window.requestAnimationFrame(() => {
      setMaxHeight(getCollapsedHeight(element))
      setIsExpanded(false)
      scheduleAnimationEnd()
    })
  }

  return {
    contentRef,
    isClamped: !isExpanded && !isAnimating,
    isExpanded,
    isTruncated,
    maxHeight,
    sectionRef,
    toggle
  }
}

export default useExpandableDescription
